import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import 'hammerjs';
import { styled } from 'styled-components';
import Helmet from '../../../commonComponents/Helmet';
import useAsyncAction from '../../../commonComponents/Hooks';
import SwitchField from '../../../commonComponents/fields/SwitchField';
import Input from '../../../commonComponents/appForm/Input';
import EmailField from '../../../commonComponents/appForm/EmailField';
import EmptyFieldForTwoColumnForm from '../../../commonComponents/appForm/EmptyFieldForTwoColumnForm';
import DropDownListField from '../../../commonComponents/appForm/DropDownListField';
import { DropDownListOption } from '../../../models/DropDownListOption';
import { createClientFromExtendedClient, createExtendedClient, ExtendedClient } from '../../../models/Client';
import { getOfficeLocationData, getDirectorsData } from '../../../lib/utils';
import { getClientByUniqueId, updateClient, createNewClient, useOffices, useDirectors } from '../../../core/api';
import { age45OrOlder, ageUnder45, bbocQuestionsPageUrl, bbocWelcomePageUrl } from '../../../core/constants';
import {
	getClientUniqueId,
	setAgeGroupToLocalStorage,
	setClientUniqueId,
	setIsBusinessOwnerToLocalStorage,
	setSurveyUniqueId
} from '../../../core/localStorage';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import AutoLogoutLayout, { SeparatorLine } from '../common/Layout';
import AppForm from '../common/AppForm';
import { brandingColourHeavy, superLightGreyColour } from '../../cssConstants';

const BbocProfilePage = () => {
	const offices = useOffices() ?? [];
	const directors = useDirectors() ?? [];
	const clientUniqueIdInLocalStorage = getClientUniqueId();
	const [client, setClient] = React.useState<ExtendedClient>(createExtendedClient(null));
	const [directorsData, setDirectorsData] = React.useState<DropDownListOption<number>[]>([]);

	const methods = useForm({
		defaultValues: React.useMemo(() => client, [client])
	});
	const { reset, control, setValue, getValues } = methods;
	const officeId = useWatch({ control, name: 'officeId', defaultValue: null });
	const isExistingClient = useWatch({ control, name: 'isClient', defaultValue: client.isClient });

	const performAsyncAction = useAsyncAction();
	const navigate = useNavigateWithAppName();
	const { signal } = new AbortController();

	React.useEffect(() => {
		performAsyncAction(async () => {
			if (clientUniqueIdInLocalStorage) {
				const savedClient = await getClientByUniqueId(clientUniqueIdInLocalStorage, signal);
				const extendedClient = createExtendedClient(savedClient);
				setClient(extendedClient);
				reset(extendedClient); // reset react-hook-form
			}
		});
	}, []);

	React.useEffect(() => {
		const newDirectorsData = getDirectorsData(directors, officeId);
		setDirectorsData(newDirectorsData);
		const directorId = getValues('directorId');
		if (!newDirectorsData.some((x) => x.value === directorId)) {
			setValue('directorId', null);
		}
	}, [officeId, directors]);

	React.useEffect(() => {
		if (!isExistingClient) {
			const values = getValues();
			const currentClient = JSON.parse(JSON.stringify(values)) as ExtendedClient;
			const updatedClient: ExtendedClient = { ...currentClient, isClient: false, officeId: null, directorId: null };
			setClient(updatedClient);
			reset(updatedClient); // reset react-hook-form
		}
	}, [isExistingClient]);

	const handleSubmit = async (extendedClient: ExtendedClient) => {
		const updatedClient = createClientFromExtendedClient({ ...client, ...extendedClient });
		if (clientUniqueIdInLocalStorage) await updateClient(clientUniqueIdInLocalStorage, updatedClient, signal);
		else {
			const clientAndSurvey = await createNewClient(updatedClient, signal);
			setClientUniqueId(clientAndSurvey.clientUniqueId);
			setSurveyUniqueId(clientAndSurvey.surveyUniqueId);
		}
		setAgeGroupToLocalStorage(updatedClient.ageGroup);
		setIsBusinessOwnerToLocalStorage(JSON.stringify(updatedClient.isBusinessOwner));
		setTimeout(() => navigate(`${bbocQuestionsPageUrl}/1`), 0);
	};

	const handleBackButtonClick = () => {
		navigate(bbocWelcomePageUrl);
	};

	return (
		<AutoLogoutLayout showFooter={false} headerText="Financial Progress Score" onBackButtonClick={null}>
			<Helmet>
				<title>Financial Progress Score</title>
			</Helmet>
			<HeaderTitle>Personal</HeaderTitle>
			<HeaderDescription>
				Please enter your personal details so we can send you your results after the challenge.
			</HeaderDescription>
			<SeparatorLine />
			<AppForm
				methods={methods}
				defaultValues={client}
				onSubmit={handleSubmit}
				onBackButtonClick={handleBackButtonClick}
				primaryButtonText="Next"
			>
				<Input label="First Name" name="firstName" maxLength={50} required />
				<Input label="Last Name" name="lastName" maxLength={50} required />
				<EmailField />
				<EmptyFieldForTwoColumnForm />
				<SwitchField label="Age group" name="isOver45" offLabel={age45OrOlder} onLabel={ageUnder45} />
				<SwitchField label="Are you a business owner?" name="isBusinessOwner" invertValue />
				<SwitchField label="Are you a Kelly+Partners client?" name="isClient" invertValue disabled />
				<EmptyFieldForTwoColumnForm />
				<DropDownListField
					label="Your Kelly+Partners Office"
					name="officeId"
					data={getOfficeLocationData(offices)}
					required
				/>
				{directorsData.length > 0 && (
					<DropDownListField
						label="Your Kelly+Partners Client Director"
						name="directorId"
						data={directorsData}
						required
					/>
				)}
			</AppForm>
		</AutoLogoutLayout>
	);
};

export default BbocProfilePage;

const HeaderTitle = styled.p`
	color: ${brandingColourHeavy};
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	letter-spacing: 0.15px;
`;

const HeaderDescription = styled.p`
	color: ${superLightGreyColour};
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.25px;
	margin: 10px 0 16px 0;
`;
